import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { decodeUrl } from '../lib/urlUtils';
import './GlobalSearch.css';

interface GlobalSearchProps {
  onGotoHistory: (query: string) => void;
  onGotoDomainAnalytics: () => void;
}

export default function GlobalSearch({ onGotoHistory, onGotoDomainAnalytics }: GlobalSearchProps) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Data sources
  const recentScans = useQuery(api.scans.getRecentScans, { limit: 100 });
  const domains = useQuery(api.domainAnalytics.searchDomains, { searchTerm: term.length >= 2 ? term : undefined, limit: 10 });
  const vio = useQuery(api.violations.searchViolations as any, { term: term.length >= 2 ? term : '', limit: 10 } as any);

  const titles = useMemo(() => {
    if (!recentScans || term.length < 2) return [] as any[];
    const t = term.toLowerCase();
    const byTitle = new Map<string, any>();
    for (const s of recentScans) {
      if (s.contentTitle?.toLowerCase().includes(t)) {
        byTitle.set(s.contentTitle, s);
      }
    }
    return Array.from(byTitle.values()).slice(0, 10);
  }, [recentScans, term]);

  useEffect(() => {
    setOpen(term.length >= 2);
  }, [term]);

  return (
    <div className="global-search" onBlur={() => setTimeout(() => setOpen(false), 150)}>
      <input
        type="search"
        placeholder="Search titles, domains, URLs…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => term.length >= 2 && setOpen(true)}
        className="global-search-input"
        aria-label="Global search"
      />

      {open && (
        <div className="global-search-panel">
          <div className="section">
            <div className="section-title">Titles</div>
            {(titles || []).length === 0 ? (
              <div className="empty">No matches</div>
            ) : (
              (titles || []).map((s) => (
                <button
                  key={s._id}
                  className="result"
                  onMouseDown={() => {
                    onGotoHistory(s.contentTitle);
                  }}
                >
                  <span className="result-primary">{s.contentTitle}</span>
                  <span className="result-secondary">{s.contentType || 'title'}</span>
                </button>
              ))
            )}
          </div>

          <div className="section">
            <div className="section-title">Domains</div>
            {(!domains || domains.length === 0) ? (
              <div className="empty">No matches</div>
            ) : (
              domains.map((d: any) => (
                <button
                  key={d.domain}
                  className="result"
                  onMouseDown={() => {
                    localStorage.setItem('domainAnalyticsSelected', d.domain);
                    onGotoDomainAnalytics();
                  }}
                >
                  <span className="result-primary">{d.domain}</span>
                  <span className="result-secondary">{d.totalViolations} violations</span>
                </button>
              ))
            )}
          </div>

          <div className="section">
            <div className="section-title">URLs</div>
            {(!vio || vio.length === 0) ? (
              <div className="empty">No matches</div>
            ) : (
              vio.map((v: any, idx: number) => (
                <a key={`${v._id}-${idx}`} className="result" href={v.url} target="_blank" rel="noopener noreferrer">
                  <span className="result-primary">{decodeUrl(v.url)}</span>
                  <span className="result-secondary">{v.domain}</span>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
